import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { StarRatingInput } from './StarRatingInput';

import { DrawerContent, DrawerTitle, Drawer } from '@/components/ui/drawer';
import { useReviewOrder } from '../services/review';
import { LoadingButton } from '../../../components/ui/loadingButton';

import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '../../../components/ui/form';

import MultiCheckboxField from '../../../components/form/ReviewComment';
import { Card } from '../../../components/ui/card';

import InputField from '../../../components/form/InputFiled';

const commentSchema = z.object({
  comment: z.array(z.string()),
});

type Comment = z.infer<typeof commentSchema>;

const commentFirstScheam = z.object({
  commentFirst: z.string().optional(),
});

type CommentFirst = z.infer<typeof commentFirstScheam>;

interface RateOrderDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  orderNumber: string;
  order: number;
}

export const RateOrderDialog: React.FC<RateOrderDialogProps> = ({
  open,
  setOpen,
  orderNumber,
  order,
}) => {
  const { t } = useTranslation();
  const commentOptions = [
    {
      label: t('Не понравился вкус блюд'),
      value: 'Не понравился вкус блюд',
    },
    {
      label: t('Неверно собран заказ'),
      value: 'Неверно собран заказ',
    },
    {
      label: t('Еда остыла'),
      value: 'Еда остыла',
    },
    {
      label: t('Нет приборов'),
      value: 'Нет приборов',
    },
    {
      label: t('Курьер опоздал'),
      value: 'Курьер опоздал',
    },
  ];
  const [rating, setRating] = useState(0);
  const [step, setStep] = useState<1 | 2>(1);
  const fomrFirst = useForm<CommentFirst>({
    resolver: zodResolver(commentFirstScheam),
  });
  const form = useForm<Comment>({
    resolver: zodResolver(commentSchema),
  });

  const { reviewOrder, isPending } = useReviewOrder();

  const handleRatingSubmit = async (data: CommentFirst) => {
    if (rating === 0) {
      console.warn('Please select a rating');
      return;
    }
    if (rating > 2) {
      await reviewOrder({
        order_id: order,
        delivery_rating: rating,
        order_rating: rating,
        comment: data.commentFirst,
      }).then(() => {
        setOpen(false);
      });
    } else if (rating <= 2) {
      setStep(2);
    }
  };

  const handleReviewSubmit = async (data: Comment) => {
    await reviewOrder({
      order_id: order,
      delivery_rating: rating,
      order_rating: rating,
      comment: data.comment.join(', '),
    }).then(() => {
      setOpen(false);
    });
  };

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerContent className="rounded-t-2xl">
        {step === 1 && (
          <div className="p-4 flex flex-col space-y-6">
            <div className="flex flex-row  items-center ">
              <DrawerTitle className="text-display-md">
                {t('rateDialog.title', { orderNumber })}
              </DrawerTitle>
            </div>

            <div className="flex flex-col items-center space-y-6 ">
              <Form {...fomrFirst}>
                <div className="flex flex-col space-y-4 w-full justify-center items-center">
                  <StarRatingInput value={rating} onChange={setRating} />
                  <InputField
                    name="commentFirst"
                    placeholder={t('comment')}
                    className="w-full h-12  border-none text-[22px] font-semibold rounded-[16px] leading-7 tracking-[0px] caret-button-primary  px-4 py-[18px]  placeholder:text-[22px] placeholder:font-semibold placeholder:text-text-primary"
                  />
                </div>
                <LoadingButton
                  loading={isPending}
                  className="h-12 w-full rounded-[12px] bg-button-primary text-white text-base font-semibold active:bg-button-primary focus:bg-button-primary disabled:bg-button-primary focus-within:bg-button-primary"
                  onClick={fomrFirst.handleSubmit(handleRatingSubmit)}
                >
                  {t('rateDialog.submitButton')}{' '}
                </LoadingButton>
              </Form>
            </div>
          </div>
        )}
        {step === 2 && (
          <div className="p-4 flex flex-col space-y-6">
            <div className="flex flex-row  items-center ">
              <DrawerTitle className="text-display-md">
                {t('rateDialog.feedbackTitle')}
              </DrawerTitle>
            </div>

            <div className="flex flex-col items-center space-y-6">
              <Form {...form}>
                <Card className="p-2 w-full">
                  <MultiCheckboxField name="comment" options={commentOptions} />
                </Card>
              </Form>

              <LoadingButton
                loading={isPending}
                className="h-12 w-full rounded-[12px] bg-button-primary text-white text-base font-semibold active:bg-button-primary focus:bg-button-primary disabled:bg-button-primary focus-within:bg-button-primary"
                onClick={form.handleSubmit(handleReviewSubmit)}
              >
                {t('rateDialog.submitButton')}{' '}
              </LoadingButton>
            </div>
          </div>
        )}
      </DrawerContent>
    </Drawer>
  );
};

export default RateOrderDialog;
